/**
 * Hook to sync Clerk session with Chrome extension
 *
 * Sets a cookie with the Convex auth token that the Chrome extension
 * can read using the chrome.cookies API. This enables the extension
 * to authenticate without the complexity of Clerk's syncHost mechanism.
 */

import { useEffect, useRef } from 'react'
import { useAuth } from '@clerk/clerk-react'

const COOKIE_NAME = 'tonesmith_extension_token'
const COOKIE_DOMAIN = '.tonesmith.io'
// Token refresh interval (45 minutes - tokens expire after ~1 hour)
const REFRESH_INTERVAL = 45 * 60 * 1000

/**
 * Sets or clears the extension session cookie
 */
function setExtensionCookie(token: string | null): void {
  if (token) {
    // Set cookie with token, expires in 1 hour
    const expires = new Date(Date.now() + 60 * 60 * 1000).toUTCString()
    document.cookie = `${COOKIE_NAME}=${token}; domain=${COOKIE_DOMAIN}; path=/; expires=${expires}; secure; samesite=lax`
  } else {
    // Clear cookie
    document.cookie = `${COOKIE_NAME}=; domain=${COOKIE_DOMAIN}; path=/; expires=Thu, 01 Jan 1970 00:00:00 GMT; secure; samesite=lax`
  }
}

/**
 * Syncs the Clerk session token to a cookie for Chrome extension access
 */
export function useExtensionSessionSync(): void {
  const { isSignedIn, getToken } = useAuth()
  const refreshIntervalRef = useRef<number | null>(null)

  useEffect(() => {
    const syncToken = async () => {
      if (!isSignedIn) {
        setExtensionCookie(null)
        return
      }

      try {
        // Get Convex-specific token for API calls
        const token = await getToken({ template: 'convex' })
        if (token) {
          setExtensionCookie(token)
        }
      } catch (error) {
        console.error('Failed to sync extension token:', error)
      }
    }

    // Sync immediately
    void syncToken()

    // Set up periodic refresh if signed in
    if (isSignedIn) {
      refreshIntervalRef.current = window.setInterval(() => {
        void syncToken()
      }, REFRESH_INTERVAL)
    }

    return () => {
      if (refreshIntervalRef.current) {
        clearInterval(refreshIntervalRef.current)
        refreshIntervalRef.current = null
      }
    }
  }, [isSignedIn, getToken])

  // Clear cookie on sign out
  useEffect(() => {
    if (!isSignedIn) {
      setExtensionCookie(null)
    }
  }, [isSignedIn])
}
